'use client';

import { useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useEvent, useLikeEvent, useShareEvent } from '@/hooks/useEvents';
import Image from 'next/image';
import Link from 'next/link';
import { Calendar, MapPin, Heart, Share2, ArrowLeft, Sparkles, Play, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Event, EventMedia } from '@/types/events';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';

export default function EventPageClient() {
  const params = useParams();
  const router = useRouter();
  const eventId = params.id as string;
  const { language } = useLanguage();
  const t = useTranslation(language);

  const [userId] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      let storedUserId = localStorage.getItem('userId');
      if (!storedUserId) {
        storedUserId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('userId', storedUserId);
      }
      return storedUserId;
    }
    return '';
  });

  const [selectedMedia, setSelectedMedia] = useState<{ mediaIndex: number } | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);

  const { data: event, isLoading, error } = useEvent(eventId);
  const likeMutation = useLikeEvent();
  const shareMutation = useShareEvent();

  useEffect(() => {
    if (showShareToast) {
      const timer = setTimeout(() => setShowShareToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showShareToast]);

  const handleLike = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!userId || !event) return;

    try {
      await likeMutation.mutateAsync({ eventId: event.eventId, userId });
    } catch (error) {
      console.error('Error liking event:', error);
      alert(t('events.failedToLike'));
    }
  };

  const handleShare = async () => {
    if (!event) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: event.title,
          text: event.description,
          url: window.location.href,
        });
        await shareMutation.mutateAsync(event.eventId);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShowShareToast(true);
        await shareMutation.mutateAsync(event.eventId);
      }
    } catch (error) {
      console.error('Error sharing event:', error);
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const hasUserLiked = (event: Event) => {
    return event.likedBy?.includes(userId) || false;
  };

  const openMediaModal = (mediaIndex: number) => {
    setSelectedMedia({ mediaIndex });
  };

  const closeMediaModal = () => {
    setSelectedMedia(null);
  };

  const navigateMedia = (direction: 'prev' | 'next') => {
    if (!selectedMedia || !event) return;
    const mediaCount = event.media.length;
    let newIndex = selectedMedia.mediaIndex;

    if (direction === 'prev') {
      newIndex = newIndex === 0 ? mediaCount - 1 : newIndex - 1;
    } else {
      newIndex = newIndex === mediaCount - 1 ? 0 : newIndex + 1;
    }

    setSelectedMedia({ mediaIndex: newIndex });
  };

  if (isLoading) {
    return (
      <div className="organic min-h-screen flex items-center justify-center">
        <div
          className="animate-spin rounded-full h-12 w-12"
          style={{ borderTop: '2px solid var(--color-accent)', borderBottom: '2px solid var(--color-accent)' }}
        />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="organic min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 style={{ margin: '0 0 var(--space-3)' }}>{t('events.notFound')}</h1>
          <p style={{ color: 'color-mix(in srgb, var(--color-text) 68%, transparent)', margin: '0 0 var(--space-4)' }}>
            The event you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Link href="/events" className="btn btn-primary">
            <ArrowLeft className="h-4 w-4" strokeWidth={2.75} />
            {t('events.backToEvents')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="organic min-h-screen">
      {/* Hero Image Section */}
      {event.media && event.media.length > 0 && event.media[0].type === 'image' && (
        <div className="relative h-[60vh] md:h-[70vh] w-full overflow-hidden" style={{ marginTop: 64 }}>
          <Image
            src={event.media[0].url}
            alt={event.title}
            fill
            priority
            className={`washed object-cover transition-all duration-700 ${imageLoaded ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-bg)] via-black/40 to-transparent" />

          {/* Floating Back Button */}
          <div className="absolute top-6 left-4 md:left-8 z-10">
            <button onClick={() => router.push('/events')} className="btn btn-secondary" style={{ background: 'color-mix(in srgb, var(--color-bg) 88%, transparent)' }}>
              <ArrowLeft className="h-4 w-4" strokeWidth={2.75} />
              <span className="hidden md:inline">{t('events.backToEvents')}</span>
            </button>
          </div>

          {/* Badges Overlay */}
          <div className="absolute top-6 right-4 md:right-8 flex flex-col z-10" style={{ gap: 6 }}>
            {event.featured && (
              <span className="tag tag-accent flex items-center gap-1" style={{ background: 'color-mix(in srgb, var(--color-accent-100) 90%, transparent)' }}>
                <Sparkles className="h-2.5 w-2.5" strokeWidth={2.75} />
                {t('events.featured')}
              </span>
            )}
            <span className={`tag ${event.eventType === 'upcoming' ? 'tag-accent-2' : 'tag-neutral'}`}>
              {event.eventType === 'upcoming' ? t('events.upcoming') : t('events.past')}
            </span>
          </div>

          {/* Title Overlay at Bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="max-w-5xl mx-auto">
              <h1 className="text-white drop-shadow-2xl animate-slide-down" style={{ fontSize: 'clamp(32px, 5vw, 56px)', margin: '0 0 var(--space-3)' }}>
                {event.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-white/90 drop-shadow-lg">
                <span className="flex items-center gap-2 bg-black/30 backdrop-blur-sm rounded-full" style={{ padding: '6px 14px', fontSize: 13.5, fontWeight: 500 }}>
                  <Calendar className="h-4 w-4" strokeWidth={2.75} />
                  {formatDate(event.eventDate)}
                </span>
                {event.location && (
                  <span className="flex items-center gap-2 bg-black/30 backdrop-blur-sm rounded-full" style={{ padding: '6px 14px', fontSize: 13.5, fontWeight: 500 }}>
                    <MapPin className="h-4 w-4" strokeWidth={2.75} />
                    {event.location}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: event.media?.length ? 48 : 'calc(64px + var(--space-8))', paddingBottom: 'var(--space-8)' }}>
        {/* No Hero Image Fallback */}
        {(!event.media || event.media.length === 0 || event.media[0].type !== 'image') && (
          <div className="mb-8">
            <button onClick={() => router.push('/events')} className="btn btn-ghost" style={{ marginBottom: 'var(--space-4)' }}>
              <ArrowLeft className="h-4 w-4" strokeWidth={2.75} />
              {t('events.backToEvents')}
            </button>
            <div className="flex items-start justify-between flex-wrap gap-4" style={{ marginBottom: 'var(--space-3)' }}>
              <div className="flex-1">
                <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', margin: '0 0 var(--space-3)' }}>{event.title}</h1>
                <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                  {event.featured && (
                    <span className="tag tag-accent flex items-center gap-1">
                      <Sparkles className="h-2.5 w-2.5" strokeWidth={2.75} />
                      {t('events.featured')}
                    </span>
                  )}
                  <span className={`tag ${event.eventType === 'upcoming' ? 'tag-accent-2' : 'tag-neutral'}`}>
                    {event.eventType === 'upcoming' ? t('events.upcoming') : t('events.past')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description Card */}
            <div className="card elev-sm" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: 'var(--space-4) var(--space-6)', borderBottom: '1px solid var(--color-divider)' }}>
                <h2 style={{ margin: 0, fontSize: 22 }}>Despre eveniment</h2>
              </div>
              <div style={{ padding: 'var(--space-6)' }}>
                <p style={{ fontSize: 16, lineHeight: 1.6, color: 'color-mix(in srgb, var(--color-text) 88%, transparent)', whiteSpace: 'pre-line', margin: 0 }}>
                  {event.description}
                </p>
              </div>
            </div>

            {/* Media Gallery */}
            {event.media && event.media.length > 0 && (
              <div className="card elev-sm" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: 'var(--space-4) var(--space-6)', borderBottom: '1px solid var(--color-divider)' }}>
                  <h2 style={{ margin: 0, fontSize: 22 }}>Galerie foto & video</h2>
                  <p style={{ fontSize: 13, color: 'color-mix(in srgb, var(--color-text) 60%, transparent)', margin: 'var(--space-1) 0 0' }}>
                    {event.media.length} {event.media.length === 1 ? 'fișier' : 'fișiere'} multimedia
                  </p>
                </div>
                <div style={{ padding: 'var(--space-6)' }}>
                  <div className="grid grid-cols-2 md:grid-cols-3" style={{ gap: 'var(--space-3)' }}>
                    {event.media.map((media: EventMedia, index: number) => (
                      <button
                        key={index}
                        onClick={() => openMediaModal(index)}
                        className="group/item relative aspect-square overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                        style={{ borderRadius: 'var(--radius-md)', border: '2px solid transparent' }}
                      >
                        {media.type === 'image' ? (
                          <Image
                            src={media.url}
                            alt={media.caption || event.title}
                            fill
                            className="washed object-cover"
                          />
                        ) : (
                          <div className="relative w-full h-full bg-black/90 flex items-center justify-center">
                            <div className="absolute inset-0 flex items-center justify-center bg-[var(--primary)]/10 group-hover/item:bg-[var(--primary)]/20 transition-colors">
                              <div className="relative">
                                <div className="absolute inset-0 bg-[var(--primary)]/20 blur-xl rounded-full animate-pulse"></div>
                                <Play className="h-12 w-12 text-white drop-shadow-lg group-hover/item:scale-125 transition-transform relative z-10" />
                              </div>
                            </div>
                            {media.thumbnail && (
                              <Image 
                                src={media.thumbnail} 
                                alt={media.caption || event.title} 
                                fill 
                                className="object-cover opacity-60"
                              />
                            )}
                          </div>
                        )}
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 flex items-end">
                          {media.caption && (
                            <p className="text-white text-xs p-3 w-full">
                              {media.caption}
                            </p>
                          )}
                        </div>
                        {/* Index Badge */}
                        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                          {index + 1}/{event.media.length}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
                </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              {/* Event Details Card */}
              <div className="card elev-sm" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-divider)' }}>
                  <h3 style={{ margin: 0, fontSize: 17, fontFamily: 'var(--font-heading)' }}>Informații despre eveniment</h3>
                </div>
                <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <div className="flex items-start gap-3">
                    <Calendar className="h-4 w-4 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-accent)', marginTop: 2 }} />
                    <div>
                      <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'color-mix(in srgb, var(--color-text) 60%, transparent)', margin: '0 0 2px' }}>Data & ora</p>
                      <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{formatDate(event.eventDate)}</p>
                    </div>
                  </div>

                  {event.location && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-accent)', marginTop: 2 }} />
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'color-mix(in srgb, var(--color-text) 60%, transparent)', margin: '0 0 2px' }}>Locație</p>
                        <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{event.location}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions Card */}
              <div className="card elev-sm" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-divider)' }}>
                  <h3 style={{ margin: 0, fontSize: 17, fontFamily: 'var(--font-heading)' }}>Spune-ne ce părere ai</h3>
                </div>
                <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <button
                    onClick={(e) => handleLike(e)}
                    disabled={likeMutation.isPending || !userId}
                    className={`btn ${hasUserLiked(event) ? '' : 'btn-secondary'} justify-between disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={hasUserLiked(event) ? { background: 'var(--color-accent)', color: 'var(--color-bg)' } : undefined}
                    title={!userId ? 'Loading...' : hasUserLiked(event) ? 'Unlike' : 'Like'}
                  >
                    <span className="flex items-center gap-2">
                      <Heart className={`h-4 w-4 ${hasUserLiked(event) ? 'fill-current' : ''} ${likeMutation.isPending ? 'animate-pulse' : ''}`} strokeWidth={2.75} />
                      {hasUserLiked(event) ? '✓ Îmi place' : 'Îmi place'}
                    </span>
                    <span style={{ fontWeight: 700 }}>{event.likes || 0}</span>
                  </button>

                  <button
                    onClick={handleShare}
                    disabled={shareMutation.isPending}
                    className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Share2 className={`h-4 w-4 ${shareMutation.isPending ? 'animate-pulse' : ''}`} strokeWidth={2.75} />
                    Distribuie cu prietenii
                  </button>
                </div>
              </div>

              {/* Stats Card */}
              <div className="card elev-sm" style={{ padding: 'var(--space-4)', background: 'var(--color-accent-100)' }}>
                <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 15, fontFamily: 'var(--font-heading)' }}>Popularitate</h3>
                <div className="grid grid-cols-2" style={{ gap: 'var(--space-3)' }}>
                  <div className="text-center card elev-sm" style={{ padding: 'var(--space-3)' }}>
                    <Heart className="h-4 w-4 mx-auto" strokeWidth={2.75} style={{ color: 'var(--color-accent)', marginBottom: 6 }} />
                    <p style={{ fontSize: 24, fontFamily: 'var(--font-heading)', margin: 0 }}>{event.likes || 0}</p>
                    <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'color-mix(in srgb, var(--color-text) 60%, transparent)', margin: 0 }}>Like-uri</p>
                  </div>
                  <div className="text-center card elev-sm" style={{ padding: 'var(--space-3)' }}>
                    <Share2 className="h-4 w-4 mx-auto" strokeWidth={2.75} style={{ color: 'var(--color-accent)', marginBottom: 6 }} />
                    <p style={{ fontSize: 24, fontFamily: 'var(--font-heading)', margin: 0 }}>{event.shares || 0}</p>
                    <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'color-mix(in srgb, var(--color-text) 60%, transparent)', margin: 0 }}>Distribuiri</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Media Modal */}
      {selectedMedia && event.media && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm animate-fade-in"
          onClick={closeMediaModal}
        >
          {/* Close Button */}
          <button 
            onClick={closeMediaModal} 
            className="absolute top-6 right-6 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all duration-300 hover:scale-110 z-50 group/close backdrop-blur-sm border border-white/20"
            aria-label="Închide"
          >
            <X className="h-7 w-7 group-hover/close:rotate-90 transition-transform duration-300" />
          </button>

          {/* Navigation Buttons */}
          {event.media.length > 1 && (
            <>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateMedia('prev');
                }} 
                className="absolute left-6 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all duration-300 hover:scale-110 z-50 group/nav backdrop-blur-sm border border-white/20"
                aria-label="Anterior"
              >
                <ChevronLeft className="h-7 w-7 group-hover/nav:-translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateMedia('next');
                }} 
                className="absolute right-6 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all duration-300 hover:scale-110 z-50 group/nav backdrop-blur-sm border border-white/20"
                aria-label="Următor"
              >
                <ChevronRight className="h-7 w-7 group-hover/nav:translate-x-1 transition-transform" />
              </button>
            </>
          )}

          {/* Media Counter */}
          <div className="absolute top-6 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-md rounded-full text-white font-semibold z-50 border border-white/20 shadow-xl">
            <span className="text-lg">{selectedMedia.mediaIndex + 1}</span>
            <span className="text-white/60 mx-1">/</span>
            <span className="text-white/80">{event.media.length}</span>
          </div>

          {/* Media Content */}
          <div 
            className="max-w-7xl max-h-[90vh] w-full px-4 md:px-8" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
            {event.media[selectedMedia.mediaIndex].type === 'image' ? (
                <div className="relative w-full h-[80vh] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/10">
                  <Image 
                    src={event.media[selectedMedia.mediaIndex].url} 
                    alt={event.media[selectedMedia.mediaIndex].caption || event.title} 
                    fill 
                    className="object-contain"
                  />
              </div>
            ) : (
                <video 
                  src={event.media[selectedMedia.mediaIndex].url} 
                  controls 
                  autoPlay 
                  className="w-full h-[80vh] object-contain rounded-2xl shadow-2xl border-2 border-white/10"
                />
              )}
              {event.media[selectedMedia.mediaIndex].caption && (
                <div className="mt-6 text-center">
                  <p className="text-white text-lg bg-black/50 backdrop-blur-md py-4 px-8 rounded-2xl inline-block border border-white/10 shadow-xl">
                    {event.media[selectedMedia.mediaIndex].caption}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Share Toast Notification */}
      {showShareToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-[var(--card)] border-2 border-[var(--primary)]/30 rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-3 backdrop-blur-sm">
            <div className="relative">
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
              <div className="absolute inset-0 w-3 h-3 rounded-full bg-green-500 animate-ping"></div>
            </div>
            <div>
              <p className="text-[var(--foreground)] font-bold text-sm">✓ Link copiat!</p>
              <p className="text-[var(--muted-foreground)] text-xs">Poți distribui acum evenimentul</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
